/**
 * Image Editor layered document model (Phase 1).
 *
 * Source of truth: ordered layer stack (bottom → top). Raster layers use
 * full-document ImageData; text layers store live params + transform.
 * Display/export canvas is a dirty-flag composite — not the editable buffer.
 *
 * History: copy-on-write. Snapshots shallow-copy the layer list and share
 * ImageData references for unchanged layers. Before mutating a layer's
 * pixels, `ensureLayerPixelsUnique` clones that layer's ImageData once per
 * history boundary so prior snapshots keep the old buffer.
 *
 * Geometry (Phase 4): Crop / Canvas Size / Image Size rewrite every layer's
 * pixels to the new document size in one op via `applyEditorDocumentGeometry`.
 * That necessarily replaces all layer ImageData buffers (accepted CoW cost for
 * infrequent document-level ops); one history entry covers the whole stack.
 *
 * Persistence (later phase): `id` + `revision` + `persistedRevision` are
 * stable hooks for autosave / session-recovery without reshaping this model.
 */

import {
  cloneEditorTextLayerContent,
  isEditorTextLayer,
  renderEditorTextLayerToDocumentPixels,
} from './imageEditorTextLayer.js';
import { rasterizeFloatToDocumentPixels } from './imageEditorFloat.js';

export { rasterizeFloatToDocumentPixels };

/**
 * @param {ImageData} imageData
 * @returns {ImageData}
 */
function cloneImageData(imageData) {
  return new ImageData(
    new Uint8ClampedArray(imageData.data),
    imageData.width,
    imageData.height,
  );
}

export const BLEND_NORMAL = 'normal';
export const BLEND_MULTIPLY = 'multiply';
export const BLEND_SCREEN = 'screen';
export const BLEND_OVERLAY = 'overlay';

/** @type {readonly string[]} */
export const EDITOR_BLEND_MODES = [
  BLEND_NORMAL,
  BLEND_MULTIPLY,
  BLEND_SCREEN,
  BLEND_OVERLAY,
];

export const EDITOR_HISTORY_CAP = 20;

export const LAYER_KIND_RASTER = 'raster';
export const LAYER_KIND_TEXT = 'text';

/**
 * @typedef {import('./imageEditorTextLayer.js').EditorTextLayerContent} EditorTextLayerContent
 */

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   visible: boolean,
 *   opacity: number,
 *   blendMode: string,
 *   pixels: ImageData,
 *   kind?: 'raster'|'text',
 *   text?: EditorTextLayerContent|null,
 * }} EditorLayer
 */

/**
 * @typedef {{
 *   id: string,
 *   width: number,
 *   height: number,
 *   layers: EditorLayer[],
 *   activeLayerId: string,
 *   revision: number,
 *   persistedRevision: number,
 *   compositeDirty: boolean,
 *   cowUniqueLayerIds: Set<string>,
 * }} EditorDocument
 */

/**
 * @typedef {{
 *   width: number,
 *   height: number,
 *   activeLayerId: string,
 *   layers: EditorLayer[],
 * }} EditorDocumentSnapshot
 */

/**
 * @returns {string}
 */
export function createEditorDocumentId() {
  return `iedoc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @returns {string}
 */
export function createEditorLayerId() {
  return `layer_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * @param {Partial<EditorLayer> & { pixels: ImageData }} partial
 * @returns {EditorLayer}
 */
export function createEditorLayer(partial) {
  const pixels = partial.pixels;
  if (!pixels) throw new Error('createEditorLayer requires pixels.');
  const kind =
    partial.kind === LAYER_KIND_TEXT ? LAYER_KIND_TEXT : LAYER_KIND_RASTER;
  /** @type {EditorLayer} */
  const layer = {
    id: partial.id || createEditorLayerId(),
    name:
      typeof partial.name === 'string' && partial.name.trim()
        ? partial.name.trim()
        : 'Layer',
    visible: partial.visible !== false,
    opacity:
      typeof partial.opacity === 'number'
        ? Math.max(0, Math.min(1, partial.opacity))
        : 1,
    blendMode:
      typeof partial.blendMode === 'string' &&
      EDITOR_BLEND_MODES.includes(partial.blendMode)
        ? partial.blendMode
        : BLEND_NORMAL,
    pixels,
    kind,
  };
  if (kind === LAYER_KIND_TEXT && partial.text) {
    layer.text = cloneEditorTextLayerContent(partial.text);
  }
  return layer;
}

/**
 * Flat image / blank canvas → single Background layer. No migration of
 * stored assets — open path only.
 *
 * @param {ImageData} imageData
 * @param {{ name?: string, documentId?: string }} [opts]
 * @returns {EditorDocument}
 */
export function createEditorDocumentFromImageData(imageData, opts = {}) {
  if (!imageData) throw new Error('imageData is required.');
  const layer = createEditorLayer({
    name: opts.name || 'Background',
    visible: true,
    opacity: 1,
    blendMode: BLEND_NORMAL,
    pixels: cloneImageData(imageData),
  });
  return {
    id: opts.documentId || createEditorDocumentId(),
    width: imageData.width,
    height: imageData.height,
    layers: [layer],
    activeLayerId: layer.id,
    revision: 0,
    persistedRevision: 0,
    compositeDirty: true,
    cowUniqueLayerIds: new Set([layer.id]),
  };
}

/**
 * @param {number} width
 * @param {number} height
 * @param {{ name?: string, documentId?: string }} [opts]
 * @returns {EditorDocument}
 */
export function createBlankEditorDocument(width, height, opts = {}) {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  return createEditorDocumentFromImageData(new ImageData(w, h), opts);
}

/**
 * @param {EditorDocument} doc
 * @returns {EditorLayer|null}
 */
export function getActiveEditorLayer(doc) {
  if (!doc?.layers?.length) return null;
  return doc.layers.find((l) => l.id === doc.activeLayerId) || doc.layers[doc.layers.length - 1];
}

/**
 * Structural snapshot: new layer objects, shared ImageData refs (CoW).
 * @param {EditorDocument} doc
 * @returns {EditorDocumentSnapshot}
 */
export function snapshotEditorDocument(doc) {
  return {
    width: doc.width,
    height: doc.height,
    activeLayerId: doc.activeLayerId,
    layers: doc.layers.map((layer) => ({
      id: layer.id,
      name: layer.name,
      visible: layer.visible,
      opacity: layer.opacity,
      blendMode: layer.blendMode,
      pixels: layer.pixels,
      kind: layer.kind || LAYER_KIND_RASTER,
      text:
        isEditorTextLayer(layer) && layer.text
          ? cloneEditorTextLayerContent(layer.text)
          : null,
    })),
  };
}

/**
 * @param {EditorDocument} doc
 * @param {EditorDocumentSnapshot} snap
 */
export function restoreEditorDocumentFromSnapshot(doc, snap) {
  doc.width = snap.width;
  doc.height = snap.height;
  doc.activeLayerId = snap.activeLayerId;
  doc.layers = snap.layers.map((layer) => ({
    id: layer.id,
    name: layer.name,
    visible: layer.visible,
    opacity: layer.opacity,
    blendMode: layer.blendMode,
    pixels: layer.pixels,
    kind: layer.kind || LAYER_KIND_RASTER,
    text:
      layer.kind === LAYER_KIND_TEXT && layer.text
        ? cloneEditorTextLayerContent(layer.text)
        : null,
  }));
  doc.compositeDirty = true;
  doc.cowUniqueLayerIds = new Set();
  doc.revision += 1;
}

/**
 * Ensure `layerId` has a private ImageData buffer not shared with history.
 * Unchanged layers keep shared refs across snapshots.
 *
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @returns {EditorLayer}
 */
export function ensureLayerPixelsUnique(doc, layerId) {
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) throw new Error('Layer not found.');
  if (doc.cowUniqueLayerIds.has(layerId)) {
    return doc.layers[index];
  }
  const prev = doc.layers[index];
  const next = {
    ...prev,
    pixels: cloneImageData(prev.pixels),
  };
  doc.layers = doc.layers.slice();
  doc.layers[index] = next;
  doc.cowUniqueLayerIds.add(layerId);
  doc.compositeDirty = true;
  return next;
}

/**
 * Call after pushing a history snapshot — next mutation must CoW again.
 * @param {EditorDocument} doc
 */
export function clearCowUniqueLayerIds(doc) {
  doc.cowUniqueLayerIds = new Set();
}

/**
 * @param {EditorDocument} doc
 * @param {string} layerId
 */
export function setActiveEditorLayer(doc, layerId) {
  if (!doc.layers.some((l) => l.id === layerId)) return;
  doc.activeLayerId = layerId;
}

/**
 * @param {EditorDocument} doc
 * @param {{
 *   name?: string,
 *   pixels?: ImageData|null,
 *   insertAboveActive?: boolean,
 * }} [opts]
 * @returns {EditorLayer}
 */
export function addEditorLayer(doc, opts = {}) {
  const pixels =
    opts.pixels ||
    new ImageData(doc.width, doc.height);
  if (pixels.width !== doc.width || pixels.height !== doc.height) {
    throw new Error('Layer pixels must match document size.');
  }
  const layer = createEditorLayer({
    name: opts.name || `Layer ${doc.layers.length + 1}`,
    pixels: cloneImageData(pixels),
  });
  const next = doc.layers.slice();
  let insertAt = next.length;
  if (opts.insertAboveActive) {
    const activeIdx = next.findIndex((l) => l.id === doc.activeLayerId);
    insertAt = activeIdx >= 0 ? activeIdx + 1 : next.length;
  }
  next.splice(insertAt, 0, layer);
  doc.layers = next;
  doc.activeLayerId = layer.id;
  doc.cowUniqueLayerIds.add(layer.id);
  doc.compositeDirty = true;
  doc.revision += 1;
  return layer;
}

/**
 * Insert a live text layer (params + transform, not baked pixels).
 *
 * @param {EditorDocument} doc
 * @param {{
 *   name?: string,
 *   text: EditorTextLayerContent,
 *   insertAboveActive?: boolean,
 *   layerId?: string,
 * }} opts
 * @returns {EditorLayer}
 */
export function addEditorTextLayer(doc, opts) {
  const content = cloneEditorTextLayerContent(opts.text);
  const layer = createEditorLayer({
    id: opts.layerId || createEditorLayerId(),
    name: opts.name || 'Text',
    pixels: new ImageData(doc.width, doc.height),
    kind: LAYER_KIND_TEXT,
    text: content,
  });
  const next = doc.layers.slice();
  let insertAt = next.length;
  if (opts.insertAboveActive) {
    const activeIdx = next.findIndex((l) => l.id === doc.activeLayerId);
    insertAt = activeIdx >= 0 ? activeIdx + 1 : next.length;
  }
  next.splice(insertAt, 0, layer);
  doc.layers = next;
  doc.activeLayerId = layer.id;
  doc.cowUniqueLayerIds.add(layer.id);
  doc.compositeDirty = true;
  doc.revision += 1;
  return layer;
}

/**
 * Update a text layer's live payload (re-edit write-back).
 *
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @param {EditorTextLayerContent} text
 */
export function patchEditorTextLayer(doc, layerId, text) {
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) return;
  const prev = doc.layers[index];
  if (!isEditorTextLayer(prev)) return;
  const next = {
    ...prev,
    text: cloneEditorTextLayerContent(text),
  };
  doc.layers = doc.layers.slice();
  doc.layers[index] = next;
  doc.compositeDirty = true;
  doc.revision += 1;
}

/**
 * Bake a text layer to raster pixels (explicit rasterize action).
 *
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @returns {boolean}
 */
export function rasterizeEditorTextLayer(doc, layerId) {
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) return false;
  const prev = doc.layers[index];
  if (!isEditorTextLayer(prev)) return false;
  const pixels = renderEditorTextLayerToDocumentPixels(prev, doc.width, doc.height);
  if (!pixels) return false;
  const next = {
    ...prev,
    kind: LAYER_KIND_RASTER,
    pixels: cloneImageData(pixels),
    text: null,
  };
  doc.layers = doc.layers.slice();
  doc.layers[index] = next;
  doc.cowUniqueLayerIds.add(layerId);
  doc.compositeDirty = true;
  doc.revision += 1;
  return true;
}

/**
 * Layer name from float origin (Phase 3 commit naming).
 * @param {string} origin
 * @param {{ labelHint?: string }} [opts]
 * @returns {string}
 */
export function layerNameFromFloatOrigin(origin, opts = {}) {
  switch (origin) {
    case 'paste':
      return 'Paste';
    case 'import':
      return 'Import';
    case 'lift':
      return 'Lift';
    case 'transform-canvas':
      return 'Transform';
    case 'generate':
      return 'Generate';
    case 'img2img':
      return 'Img2Img';
    case 'inpaint':
      return 'Inpaint';
    case 'text': {
      const hint =
        typeof opts.labelHint === 'string' ? opts.labelHint.trim() : '';
      return hint ? `Text: ${hint.slice(0, 28)}` : 'Text';
    }
    case 'path':
      return 'Path';
    default:
      return 'Layer';
  }
}

/**
 * Sample one pixel from a composite (or any ImageData).
 * @param {ImageData} imageData
 * @param {number} x
 * @param {number} y
 * @returns {{ r: number, g: number, b: number, a: number }|null}
 */
export function sampleImageDataPixel(imageData, x, y) {
  if (!imageData) return null;
  const ix = Math.round(x);
  const iy = Math.round(y);
  if (ix < 0 || iy < 0 || ix >= imageData.width || iy >= imageData.height) {
    return null;
  }
  const i = (iy * imageData.width + ix) * 4;
  const d = imageData.data;
  return { r: d[i], g: d[i + 1], b: d[i + 2], a: d[i + 3] };
}

/**
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @param {Partial<Pick<EditorLayer,'visible'|'opacity'|'blendMode'|'name'>>} patch
 */
export function patchEditorLayer(doc, layerId, patch) {
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) return;
  const prev = doc.layers[index];
  const next = { ...prev };
  if (typeof patch.name === 'string' && patch.name.trim()) {
    next.name = patch.name.trim();
  }
  if (typeof patch.visible === 'boolean') next.visible = patch.visible;
  if (typeof patch.opacity === 'number') {
    next.opacity = Math.max(0, Math.min(1, patch.opacity));
  }
  if (
    typeof patch.blendMode === 'string' &&
    EDITOR_BLEND_MODES.includes(patch.blendMode)
  ) {
    next.blendMode = patch.blendMode;
  }
  doc.layers = doc.layers.slice();
  doc.layers[index] = next;
  doc.compositeDirty = true;
  doc.revision += 1;
}

/**
 * Replace a raster layer's document-sized pixels (re-transform write-back).
 * Refuses text layers — those use patchEditorTextLayer.
 *
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @param {ImageData} pixels
 * @returns {boolean}
 */
export function setEditorLayerPixels(doc, layerId, pixels) {
  if (!doc || !pixels) return false;
  if (pixels.width !== doc.width || pixels.height !== doc.height) {
    return false;
  }
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) return false;
  const prev = doc.layers[index];
  if (isEditorTextLayer(prev)) return false;
  doc.layers = doc.layers.slice();
  doc.layers[index] = {
    ...prev,
    pixels: cloneImageData(pixels),
  };
  doc.cowUniqueLayerIds.add(layerId);
  doc.compositeDirty = true;
  doc.revision += 1;
  return true;
}

/**
 * Decide whether an inline rename should persist.
 * Used by the Layers panel blur/Enter path (and Esc cancel).
 *
 * @param {{
 *   cancel?: boolean,
 *   nextName?: string,
 *   originalName?: string,
 * }} params
 * @returns {{ commit: true, name: string } | { commit: false, reason: 'cancel'|'noop'|'empty' }}
 */
export function resolveLayerRenameCommit(params) {
  if (params.cancel) return { commit: false, reason: 'cancel' };
  const original =
    typeof params.originalName === 'string' ? params.originalName : '';
  const trimmed =
    typeof params.nextName === 'string' ? params.nextName.trim() : '';
  if (!trimmed) return { commit: false, reason: 'empty' };
  if (trimmed === original) return { commit: false, reason: 'noop' };
  return { commit: true, name: trimmed };
}

/**
 * Remove a layer. Refuses when it would leave the document empty.
 * If the active layer is removed, selects the layer below it (or the new
 * bottom-most / remaining neighbor).
 *
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function deleteEditorLayer(doc, layerId) {
  if (!doc?.layers?.length) {
    return { ok: false, reason: 'No layers.' };
  }
  if (doc.layers.length <= 1) {
    return { ok: false, reason: 'Cannot delete the last layer.' };
  }
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) {
    return { ok: false, reason: 'Layer not found.' };
  }
  const wasActive = doc.activeLayerId === layerId;
  doc.layers = doc.layers.filter((l) => l.id !== layerId);
  if (wasActive) {
    const fallbackIndex = Math.min(index, doc.layers.length - 1);
    const belowIndex = Math.max(0, index - 1);
    // Prefer the layer that was below (lower in stack); if deleted was bottom,
    // take the layer that slid into index 0 / former above.
    const pick =
      index > 0 ? doc.layers[belowIndex] : doc.layers[fallbackIndex];
    doc.activeLayerId = pick.id;
  }
  doc.cowUniqueLayerIds.delete(layerId);
  doc.compositeDirty = true;
  doc.revision += 1;
  return { ok: true };
}

/**
 * Duplicate a layer directly above the source (higher stack index = on top).
 * Pixel buffer is always a deep clone — never shares ImageData with source.
 *
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @returns {EditorLayer|null}
 */
export function duplicateEditorLayer(doc, layerId) {
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index < 0) return null;
  const src = doc.layers[index];
  /** @type {EditorLayer} */
  const copy = isEditorTextLayer(src)
    ? createEditorLayer({
        name: `${src.name} copy`,
        visible: src.visible,
        opacity: src.opacity,
        blendMode: src.blendMode,
        pixels: cloneImageData(src.pixels),
        kind: LAYER_KIND_TEXT,
        text: cloneEditorTextLayerContent(src.text),
      })
    : createEditorLayer({
        name: `${src.name} copy`,
        visible: src.visible,
        opacity: src.opacity,
        blendMode: src.blendMode,
        pixels: cloneImageData(src.pixels),
      });
  const next = doc.layers.slice();
  next.splice(index + 1, 0, copy);
  doc.layers = next;
  doc.activeLayerId = copy.id;
  doc.cowUniqueLayerIds.add(copy.id);
  doc.compositeDirty = true;
  doc.revision += 1;
  return copy;
}

/**
 * Move a layer to a new index in the bottom→top stack.
 * @param {EditorDocument} doc
 * @param {string} layerId
 * @param {number} toIndex
 * @returns {boolean}
 */
export function reorderEditorLayer(doc, layerId, toIndex) {
  const from = doc.layers.findIndex((l) => l.id === layerId);
  if (from < 0) return false;
  const bounded = Math.max(0, Math.min(doc.layers.length - 1, Math.round(toIndex)));
  if (from === bounded) return false;
  const next = doc.layers.slice();
  const [item] = next.splice(from, 1);
  next.splice(bounded, 0, item);
  doc.layers = next;
  doc.compositeDirty = true;
  doc.revision += 1;
  return true;
}

/**
 * Replace document with a flat ImageData (single Background layer).
 * Used for whole-canvas transform promote/discard and new blank resets —
 * not for Crop / Canvas Size / Image Size (see applyEditorDocumentGeometry).
 *
 * @param {EditorDocument} doc
 * @param {ImageData} imageData
 */
export function replaceEditorDocumentFlat(doc, imageData) {
  const layer = createEditorLayer({
    name: 'Background',
    pixels: cloneImageData(imageData),
  });
  doc.width = imageData.width;
  doc.height = imageData.height;
  doc.layers = [layer];
  doc.activeLayerId = layer.id;
  doc.cowUniqueLayerIds = new Set([layer.id]);
  doc.compositeDirty = true;
  doc.revision += 1;
}

/**
 * Apply the same pixel transform to every layer and update doc size.
 * Preserves layer ids / names / visibility / opacity / blendMode / order.
 * New ImageData per layer (geometry always diverges all buffers from history —
 * accepted CoW cost for infrequent document-level ops).
 *
 * Used by Crop / Canvas Size / Image Size (Phase 4). Callers supply the
 * per-buffer mapper (typically cropImageData / resizeCanvasImageData /
 * scaleImageData).
 *
 * @param {EditorDocument} doc
 * @param {(pixels: ImageData) => ImageData|null} mapPixels
 * @returns {boolean}
 */
export function applyEditorDocumentGeometry(doc, mapPixels) {
  if (!doc?.layers?.length || typeof mapPixels !== 'function') return false;
  /** @type {EditorLayer[]} */
  const nextLayers = [];
  let nextW = 0;
  let nextH = 0;
  for (const layer of doc.layers) {
    const mapped = mapPixels(layer.pixels);
    if (!mapped) return false;
    if (nextLayers.length === 0) {
      nextW = mapped.width;
      nextH = mapped.height;
    } else if (mapped.width !== nextW || mapped.height !== nextH) {
      return false;
    }
    nextLayers.push({
      ...layer,
      pixels: mapped,
    });
  }
  doc.width = nextW;
  doc.height = nextH;
  doc.layers = nextLayers;
  doc.cowUniqueLayerIds = new Set(nextLayers.map((l) => l.id));
  doc.compositeDirty = true;
  doc.revision += 1;
  return true;
}

/**
 * Channel blend helpers (src over dst with mode, then opacity).
 * Inputs/outputs are 0–255 channels; alpha handled by caller.
 */

/**
 * @param {number} cb base 0–1
 * @param {number} cs blend 0–1
 * @param {string} mode
 * @returns {number} 0–1
 */
export function blendChannel(cb, cs, mode) {
  switch (mode) {
    case BLEND_MULTIPLY:
      return cb * cs;
    case BLEND_SCREEN:
      return 1 - (1 - cb) * (1 - cs);
    case BLEND_OVERLAY:
      return cb < 0.5 ? 2 * cb * cs : 1 - 2 * (1 - cb) * (1 - cs);
    case BLEND_NORMAL:
    default:
      return cs;
  }
}

/**
 * @param {ImageData} out
 * @param {ImageData} src
 * @param {number} opacity
 * @param {string} mode
 */
function blendLayerPixelsInto(out, src, opacity, mode) {
  const od = out.data;
  const sd = src.data;
  for (let i = 0; i < od.length; i += 4) {
    const sa = (sd[i + 3] / 255) * opacity;
    if (sa <= 0) continue;
    const da = od[i + 3] / 255;
    const sr = sd[i] / 255;
    const sg = sd[i + 1] / 255;
    const sb = sd[i + 2] / 255;
    const dr = od[i] / 255;
    const dg = od[i + 1] / 255;
    const db = od[i + 2] / 255;

    const br = blendChannel(dr, sr, mode);
    const bg = blendChannel(dg, sg, mode);
    const bb = blendChannel(db, sb, mode);

    const outA = sa + da * (1 - sa);
    if (outA <= 0) {
      od[i] = 0;
      od[i + 1] = 0;
      od[i + 2] = 0;
      od[i + 3] = 0;
      continue;
    }
    const outR = (br * sa + dr * da * (1 - sa)) / outA;
    const outG = (bg * sa + dg * da * (1 - sa)) / outA;
    const outB = (bb * sa + db * da * (1 - sa)) / outA;
    od[i] = Math.round(outR * 255);
    od[i + 1] = Math.round(outG * 255);
    od[i + 2] = Math.round(outB * 255);
    od[i + 3] = Math.round(outA * 255);
  }
}

/**
 * Composite visible layers bottom→top into a new ImageData.
 * @param {EditorDocument} doc
 * @param {{ omitLayerIds?: string[] }} [opts]
 * @returns {ImageData}
 */
export function compositeEditorDocument(doc, opts = {}) {
  const { width, height, layers } = doc;
  const omit = new Set(
    Array.isArray(opts.omitLayerIds)
      ? opts.omitLayerIds.filter((id) => typeof id === 'string')
      : [],
  );
  const out = new ImageData(width, height);

  for (const layer of layers) {
    if (!layer.visible || layer.opacity <= 0) continue;
    if (omit.has(layer.id)) continue;
    let src = layer.pixels;
    if (isEditorTextLayer(layer)) {
      src = renderEditorTextLayerToDocumentPixels(layer, width, height);
    }
    if (!src || src.width !== width || src.height !== height) continue;
    blendLayerPixelsInto(out, src, layer.opacity, layer.blendMode || BLEND_NORMAL);
  }
  return out;
}

/**
 * Present composite onto a canvas if dirty (or force).
 * @param {EditorDocument} doc
 * @param {HTMLCanvasElement} canvas
 * @param {{ force?: boolean }} [opts]
 * @returns {boolean} true if presented
 */
export function presentEditorDocument(doc, canvas, opts = {}) {
  if (!doc || !canvas) return false;
  if (!opts.force && !doc.compositeDirty) return false;
  if (canvas.width !== doc.width || canvas.height !== doc.height) {
    canvas.width = doc.width;
    canvas.height = doc.height;
  }
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  const composite = compositeEditorDocument(doc, {
    omitLayerIds: opts.omitLayerIds,
  });
  ctx.putImageData(composite, 0, 0);
  doc.compositeDirty = false;
  return true;
}

/**
 * CoW history stack for EditorDocument.
 * @param {number} [cap]
 */
export function createEditorDocumentHistory(cap = EDITOR_HISTORY_CAP) {
  return {
    cap,
    /** @type {EditorDocumentSnapshot[]} */
    stack: [],
    index: -1,
  };
}

/**
 * @param {{ cap: number, stack: EditorDocumentSnapshot[], index: number }} hist
 * @param {EditorDocument} doc
 */
export function pushEditorDocumentHistory(hist, doc) {
  let { stack, index } = hist;
  if (index < stack.length - 1) {
    stack = stack.slice(0, index + 1);
  }
  stack.push(snapshotEditorDocument(doc));
  while (stack.length > hist.cap) {
    stack.shift();
  }
  hist.stack = stack;
  hist.index = stack.length - 1;
  clearCowUniqueLayerIds(doc);
  doc.revision += 1;
}

/**
 * @param {{ stack: EditorDocumentSnapshot[], index: number }} hist
 * @param {EditorDocument} doc
 * @returns {boolean}
 */
export function undoEditorDocumentHistory(hist, doc) {
  if (hist.index <= 0) return false;
  hist.index -= 1;
  restoreEditorDocumentFromSnapshot(doc, hist.stack[hist.index]);
  return true;
}

/**
 * @param {{ stack: EditorDocumentSnapshot[], index: number }} hist
 * @param {EditorDocument} doc
 * @returns {boolean}
 */
export function redoEditorDocumentHistory(hist, doc) {
  if (hist.index < 0 || hist.index >= hist.stack.length - 1) return false;
  hist.index += 1;
  restoreEditorDocumentFromSnapshot(doc, hist.stack[hist.index]);
  return true;
}

/**
 * Evidence helper: unchanged layer pixel buffers are === across snaps;
 * the mutated layer's buffers are !== .
 *
 * @param {EditorDocumentSnapshot} before
 * @param {EditorDocumentSnapshot} after
 * @param {string} changedLayerId
 * @returns {{ ok: boolean, shared: string[], diverged: string[], detail: string }}
 */
export function analyzeCowSharing(before, after, changedLayerId) {
  /** @type {string[]} */
  const shared = [];
  /** @type {string[]} */
  const diverged = [];
  const beforeMap = new Map(before.layers.map((l) => [l.id, l]));
  for (const layer of after.layers) {
    const prev = beforeMap.get(layer.id);
    if (!prev) continue;
    if (prev.pixels === layer.pixels) shared.push(layer.id);
    else diverged.push(layer.id);
  }
  const changedDiverged = diverged.includes(changedLayerId);
  const othersShared = after.layers
    .filter((l) => l.id !== changedLayerId)
    .every((l) => shared.includes(l.id));
  const ok = changedDiverged && othersShared;
  return {
    ok,
    shared,
    diverged,
    detail: ok
      ? `CoW OK: layer ${changedLayerId} cloned; ${shared.length} layer(s) shared by reference.`
      : `CoW FAIL: diverged=[${diverged.join(',')}] shared=[${shared.join(',')}] changed=${changedLayerId}`,
  };
}
